/**
 * Order Detail Page
 * Show full order details with items, shipping, status
 */

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Container, Main, Flex } from '../../../components/styled/Layout';
import { Card, CardContent } from '../../../components/styled/Card';
import { PrimaryButton, SecondaryButton } from '../../../components/styled/Button';
import orderService from '../services/orderService';
import useShopFeatures from '../hooks/useShopFeatures';
import './Shop.css';

const OrderDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const features = useShopFeatures();
  const isSuccess = searchParams.get('success') === 'true';

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const data = await orderService.getOrder(id);
      setOrder(data);
    } catch (error) {
      console.error('Error loading order:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Main>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading order...</p>
          </div>
        </Main>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <Main>
          <div className="empty-state">
            <h3>Order not found</h3>
            <p>The order you're looking for doesn't exist.</p>
            <SecondaryButton as={Link} to="/shop/orders">
              Back to Orders
            </SecondaryButton>
          </div>
        </Main>
      </Container>
    );
  }

  const items = typeof order.items === 'string' ? JSON.parse(order.items) : (order.items || []);
  const shippingAddress = typeof order.shippingAddress === 'string' ? JSON.parse(order.shippingAddress) : (order.shippingAddress || {});

  return (
    <Container>
      <Main>
        <div className="shop-page order-detail-page">
          {isSuccess && (
            <Card style={{ background: '#D4EDDA', border: '1px solid #C3E6CB', marginBottom: '24px' }}>
              <CardContent>
                <h2 style={{ color: '#155724', margin: 0 }}>✅ Order Placed Successfully!</h2>
                <p style={{ color: '#155724', margin: '8px 0 0 0' }}>
                  Thank you for your order. We'll send you a confirmation email shortly.
                </p>
              </CardContent>
            </Card>
          )}

          <h1>Order {order.displayOrderId || order.id.substring(0, 8)}</h1>
          <p className="order-date">
            Placed on {new Date(order.createdAt).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </p>

          <Card style={{ marginBottom: '20px' }}>
            <CardContent>
              <h2>Order Items</h2>
              <div className="order-items-list">
                {items.map((item, idx) => (
                  <Flex key={idx} justify="space-between" style={{ padding: '16px 0', borderBottom: idx < items.length - 1 ? '1px solid var(--ui-border)' : 'none' }}>
                    <div>
                      <strong>{item.name}</strong>
                      {item.variation && (
                        <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                          {Object.entries(item.variation).map(([k, v]) => `${k}: ${v}`).join(', ')}
                        </div>
                      )}
                      <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        Quantity: {item.quantity} × ${item.price?.toFixed(2)}
                      </div>
                    </div>
                    <div style={{ fontWeight: 'bold' }}>
                      ${((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                    </div>
                  </Flex>
                ))}
              </div>

              <div className="order-total-section">
                <Flex justify="flex-end" style={{ fontSize: '20px', fontWeight: 'bold', marginTop: '20px' }}>
                  <span>Total: ${order.total?.toFixed(2) || '0.00'}</span>
                </Flex>
              </div>
            </CardContent>
          </Card>

          <Card style={{ marginBottom: '20px' }}>
            <CardContent>
              <h2>Shipping Address</h2>
              <p>
                {shippingAddress.fullName}<br />
                {shippingAddress.address}<br />
                {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zipCode}
                {shippingAddress.phone && (
                  <>
                    <br />Phone: {shippingAddress.phone}
                  </>
                )}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <h2>Order Status</h2>
              <div className={`status-badge status-${order.status}`}>
                {order.status?.toUpperCase() || 'PROCESSING'}
              </div>
              {features.orderTracking && order.trackingInfo && (
                <div style={{ marginTop: '16px' }}>
                  <p>Tracking Number: {order.trackingInfo}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Flex gap="12px" style={{ marginTop: '24px' }}>
            <SecondaryButton as={Link} to="/shop/orders">
              Back to Orders
            </SecondaryButton>
            {features.invoices && (
              <PrimaryButton onClick={() => orderService.downloadInvoice(order.id)}>
                Download Invoice
              </PrimaryButton>
            )}
          </Flex>
        </div>
      </Main>
    </Container>
  );
};

export default OrderDetail;

